import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { Pagination, Card, Collapse, Form, Input, InputNumber, Modal, Radio, Select, Tabs, message, Empty, Checkbox, Divider, Skeleton } from 'antd'
import dayjs from 'dayjs'
import { httpDelete, post, get } from 'utils/request'
import useAdmin, { commonSWROptions } from '@/hooks/swr/useAdmin'
import { useMatcheslist } from '@/hooks/swr/useMatch'
import useLogin from '@/hooks/swr/useLogin'
import VipInfo from '@/components/VipInfo'
import WujiePromoBar from '@/components/WujiePromoBar'
import { useRouter } from 'next/router'
import Link from 'next/link'
import {
  VIP_LEVEL,
  cardMatchTypeOrder,
  getMatchLink,
  isSinglePlayerMatch,
  matchTypeIntro,
  matchTypeMap,
  onlineMatchTypeMap,
  otherMatchTypeOrder,
} from '@/utils/utils'
import { debouncedFunction } from '@/utils/debouncedFunction'
import Button from '@/components/Button'
import IconFont from '@/components/IconFonts'
import CustomedDatePicker from '@/components/CustomedDatePicker'
import { motion } from 'framer-motion'
import HomeBackground from '@/components/HomePage/HomeBackground'

// 建赛价格不再在前端硬编码 —— 一律读 `matches/quota` 返回的 prices 表(后端 MatchQuota.price_table)。
// 以前这里写死三档价格，结果中华侨牌/双升/四人手抓牌等几个赛制的标价长期与后端不一致。

const PERSONAL_MATCHES_LIMIT = 6

const getMatchScale = (match) => {
  // 后端仅对管理员/自己的比赛返回 teams_count/players_count;公开列表这两个字段缺省(undefined)。
  // 缺省时不要渲染出 "NaN人"/"undefined人",直接返回空串隐藏人数。
  const count = isSinglePlayerMatch(match.match_type)
    ? match.players_count
    : match.match_type === 'duplicated_team'
    ? match.teams_count * 4
    : match.match_type === 'zhong_hua_qiao_pai'
    ? match.teams_count * 3
    : match.teams_count * 2
  return Number.isFinite(count) ? `${count}人` : ''
}

const Matches: React.FC = ({ page, limit, match_type, keywords }: { page: number; limit: number; match_type: string; keywords: string }) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [templateModalVisible, setTemplateModalVisible] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<'list' | 'card'>('card')
  // 记录加载失败的 poster(无效链接):用默认图渲染,并把它们排到尾部
  const [brokenPosters, setBrokenPosters] = useState<Set<string>>(new Set())
  const [selectionMode, setSelectionMode] = useState<boolean>(false)
  const [selectedMatches, setSelectedMatches] = useState<string[]>([])
  // 默认值与表单 initialValues.type 保持一致,否则刚打开弹窗时「赛制介绍」和价格都是空的
  const [selectedMatchType, setSelectedMatchType] = useState<string>('team')
  const [gameCategory, setGameCategory] = useState<'card' | 'pickleball'>('card')
  // 赛事性质放在创建流程第一步:正式赛与测试赛的保存策略/计费完全不同,先选它后面的信息才有意义。
  const [isTestMatch, setIsTestMatch] = useState<boolean>(false)
  const [coreRules, setCoreRules] = useState<Array<{ slug: string; title: string }>>([])
  const [createMatchForm] = Form.useForm()
  const [myMatchesPage, setMyMatchesPage] = useState<number>(1)
  // 全部比赛排序:default=橱窗式(有海报/中文名优先),created=按创建时间倒序。记忆到 localStorage。
  const [sortBy, setSortBy] = useState<'default' | 'created'>(() => {
    if (typeof window !== 'undefined') {
      const s = window.localStorage.getItem('match_sort')
      if (s === 'created' || s === 'default') return s
    }
    return 'default'
  })
  const changeSort = (v: 'default' | 'created') => {
    setSortBy(v)
    try {
      window.localStorage.setItem('match_sort', v)
    } catch {
      /* ignore */
    }
  }

  const createMatch = (params) => post('matches', params)

  // 创建比赛的额度与价目表，打开创建弹窗时刷新。
  // prices 由后端 MatchQuota 下发 —— 前端不再自己抄一份价格，避免两边对不上。
  const [quota, setQuota] = useState<{
    vip_level: number
    tier_label: string
    needs_mobile: boolean
    required_levels: Record<string, number>
    scale: { free_under: number; block_size: number; block_fee: number }
    formal: { limit: number; used: number; remaining: number }
    test: { free: boolean; limit: number; used: number; remaining: number; ttl_minutes: number }
    prices: { required: Record<string, number>; formal: Record<string, number>; test: Record<string, number> }
  } | null>(null)

  const refreshQuota = useCallback(() => {
    get('matches/quota').then((res) => {
      if (res?.ok) setQuota(res.data)
    })
  }, [])

  useEffect(() => {
    if (modalVisible) refreshQuota()
  }, [modalVisible, refreshQuota])

  useEffect(() => {
    get('rules', { game: 'guandan' }).then((res) => {
      if (res.ok) setCoreRules(res.rules)
    })
  }, [])
  const router = useRouter()

  // 创建失败统一处理：命中单日上限时弹醒目说明 + 升级引导，其余用 toast。
  const showCreateError = (msg: string) => {
    refreshQuota()
    // 测试赛日限对所有等级一样，升级会员不会提额，别给升级引导。
    if (msg && msg.includes('场测试赛')) {
      Modal.info({
        title: '今日测试赛已达上限',
        content: (
          <div>
            <p>{msg}</p>
            <p className="mt-2 mb-0 text-gray-500">测试赛每日上限不分会员等级，额度每日 0 点重置；要继续办赛请创建正式比赛。</p>
          </div>
        ),
        okText: '知道了',
      })
    } else if (msg && msg.includes('每日最多创建')) {
      Modal.confirm({
        title: '今日创建比赛已达上限',
        content: (
          <div>
            <p>{msg}</p>
            <p className="mt-2 text-gray-500">额度每日 0 点自动重置；升级 VIP 可提高每日创建上限。</p>
          </div>
        ),
        okText: '升级 VIP 提额',
        // 升级会员走 VIP 套餐页(/vip_billing),不是点数充值页(/billing)
        cancelText: '知道了',
        onOk: () => router.push('/vip_billing'),
      })
    } else {
      message.error(msg, 15)
    }
  }

  // 建赛计价全部来自后端 quota.prices，前端只负责显示；线上赛按人次结算，不在这张表里。
  const testTtlMinutes = quota?.test?.ttl_minutes ?? 60
  const costOfType = (type?: string) => {
    if (!type || type.startsWith('online_')) return 0
    const table = isTestMatch ? quota?.prices?.test : quota?.prices?.formal
    return table?.[type] ?? 0
  }
  const currentCost = costOfType(selectedMatchType ?? createMatchForm.getFieldValue('type') ?? 'team')

  // 测试赛创建成功后的明确告知——把确切的删除时刻写出来，避免用户以为能一直用。
  const notifyTestMatchCreated = (match) => {
    const deleteAt = match?.test_expires_at ? dayjs(match.test_expires_at) : dayjs().add(testTtlMinutes, 'minute')
    Modal.warning({
      title: '测试赛已创建，请注意存活时间',
      content: (
        <div className="text-sm leading-relaxed">
          <p className="mb-2">
            本场为<b>测试赛</b>，将于 <b className="text-[#DB3142]">{deleteAt.format('YYYY-MM-DD HH:mm')}</b>{' '}
            自动删除（约 {testTtlMinutes} 分钟后）。
          </p>
          <p className="mb-0 text-gray-500">删除后选手、对局与成绩一并移除，且测试赛不可转为正式比赛。要办真实赛事请另建一场正式比赛。</p>
        </div>
      ),
      okText: '我知道了',
    })
  }

  const normalizedMatchType = match_type || 'all'

  const {
    data: { matches, sum },
    mutate,
    isValidating,
  } = useMatcheslist(page, limit, normalizedMatchType, keywords, normalizedMatchType === 'all' ? sortBy : undefined)

  const { isAdmin } = useAdmin()
  const { isLogin, vipLevel, current_user } = useLogin()

  useEffect(() => {
    if (!isLogin) {
      setMyMatchesPage(1)
    }
  }, [isLogin])

  const personalMatchesKey = isLogin && current_user?.id ? `my-matches-${current_user.id}-${PERSONAL_MATCHES_LIMIT}-${myMatchesPage}` : null
  const { data: myMatchesData = { matches: [], sum: 0 }, isValidating: isMyMatchesValidating } = useSWR(
    personalMatchesKey,
    () =>
      get('matches/matches_list', {
        page: myMatchesPage,
        pageSize: PERSONAL_MATCHES_LIMIT,
        matchesType: 'my',
        filterKeywords: '',
      }).then((res) => {
        if (res.ok) {
          return { matches: res.matches, sum: res.sum }
        }
        return { matches: [], sum: 0 }
      }),
    {
      fallbackData: { matches: [], sum: 0 },
      ...commonSWROptions,
    }
  )
  const totalMyMatches = myMatchesData?.sum ?? 0

  useEffect(() => {
    if (!personalMatchesKey || isMyMatchesValidating) {
      return
    }
    if (!totalMyMatches) {
      if (myMatchesPage !== 1) {
        setMyMatchesPage(1)
      }
      return
    }
    const maxPage = Math.max(1, Math.ceil(totalMyMatches / PERSONAL_MATCHES_LIMIT))
    if (myMatchesPage > maxPage) {
      setMyMatchesPage(maxPage)
    }
  }, [personalMatchesKey, isMyMatchesValidating, totalMyMatches, myMatchesPage])

  const onDeleteMultipleMatches = useCallback(() => {
    if (selectedMatches.length === 0) return

    Modal.confirm({
      title: '批量删除比赛',
      content: `确定要删除已选择的 ${selectedMatches.length} 场比赛吗？`,
      onOk: async () => {
        const deletePromises = selectedMatches.map((id) => httpDelete(`matches/${id}`, { data: {} }))

        await Promise.all(deletePromises)
        setSelectedMatches([])
        mutate()
        message.success(`成功删除 ${selectedMatches.length} 场比赛`)
        setSelectionMode(false)
      },
    })
  }, [selectedMatches, mutate])

  const toggleMatchSelection = useCallback((id: string) => {
    setSelectedMatches((prev) => {
      if (prev.includes(id)) {
        return prev.filter((matchId) => matchId !== id)
      }
      return [...prev, id]
    })
  }, [])

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev)
    if (selectionMode) {
      setSelectedMatches([])
    }
  }, [selectionMode])

  const tableHeader = useMemo(() => {
    return (
      <div className="mb-4 hidden rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-3 lg:block">
        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-600">
          <div className="col-span-5 flex items-center">比赛名称</div>
          <div className="col-span-2 flex items-center">时间</div>
          <div className="col-span-1 flex items-center">人数</div>
          <div className="col-span-2 flex items-center">赛制</div>
          {isAdmin && <div className="col-span-2 flex items-center">创建者</div>}
        </div>
      </div>
    )
  }, [isAdmin])

  const getMatchTypeColor = (matchType) => {
    const colors = {
      team: 'from-blue-600 to-blue-700 text-white',
      single: 'from-green-600 to-green-700 text-white',
      circle: 'from-purple-600 to-purple-700 text-white',
      spin: 'from-pink-600 to-pink-700 text-white',
      online_spin: 'from-cyan-600 to-cyan-700 text-white',
      single_public: 'from-amber-600 to-amber-700 text-white',
      excess: 'from-red-600 to-red-700 text-white',
      duplicated: 'from-indigo-600 to-indigo-700 text-white',
      duplicated_single: 'from-lime-600 to-lime-700 text-white',
      duplicated_team: 'from-orange-600 to-orange-700 text-white',
      duplicated_switzer: 'from-teal-600 to-teal-700 text-white',
      duplicated_spin: 'from-fuchsia-600 to-fuchsia-700 text-white',
      league: 'from-emerald-600 to-emerald-700 text-white',
      banquet: 'from-purple-500 to-pink-600 text-white',
    }
    return colors[matchType] || 'from-gray-600 to-gray-700 text-white'
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedViewMode = localStorage.getItem('matchesViewMode')
      if (savedViewMode === 'list' || savedViewMode === 'card') {
        setViewMode(savedViewMode)
      }
    }
  }, [])

  const handleViewModeChange = (mode: 'list' | 'card') => {
    setViewMode(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('matchesViewMode', mode)
    }
  }

  const getMatchStatus = useCallback(
    (match) => {
      if (match.finished) {
        return (
          <Button
            className="bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700"
            onClick={(e) => {
              e.preventDefault()
              router.push(getMatchLink(match.id, match.match_type))
            }}
          >
            成绩排名
          </Button>
        )
      }
      if (match.stop_entry) {
        return (
          <Button
            className="bg-yellow-100 px-2.5 py-1 text-xs font-medium text-yellow-700"
            onClick={(e) => {
              e.preventDefault()
              router.push(`/match/${match.id}`)
            }}
          >
            比赛桌次
          </Button>
        )
      }
      if (!match.show_entry_path) {
        return null
      }
      return (
        <Button
          className="bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700"
          onClick={(e) => {
            e.preventDefault()
            router.push(`/match/${match.id}/entry`)
          }}
        >
          立即报名
        </Button>
      )
    },
    [router]
  )

  const renderListView = () => {
    return (
      <motion.div key="list-view" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="space-y-4">
        {tableHeader}

        {matches.length === 0 && isValidating && (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-lg bg-white p-4 shadow-sm">
                <Skeleton active paragraph={{ rows: 1 }} title={{ width: '40%' }} />
              </div>
            ))}
          </div>
        )}

        {matches.length === 0 && !isValidating && (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-12">
            <Empty description="暂无比赛" />
            <Button type="primary" className="accent-button mt-4" onClick={() => (isLogin ? setModalVisible(true) : router.push(`/login?redirect=${router.asPath}`))}>
              创建第一个比赛
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {matches.map((match, index) => (
            <motion.div
              key={match.id}
              initial={false}
              className="group relative"
            >
              <div className="flex items-center">
                {selectionMode && (
                  <div className="mr-3 flex-shrink-0">
                    <Checkbox
                      checked={selectedMatches.includes(match.id)}
                      onChange={() => toggleMatchSelection(match.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                <Link href={getMatchLink(match.id, match.match_type)} className="w-full" onClick={(e) => selectionMode && (e.preventDefault(), toggleMatchSelection(match.id))}>
                  <div className="relative overflow-hidden rounded-lg border border-gray-100 bg-white transition-all">
                    {/* 内容区域 */}
                    <div className="grid grid-cols-12 gap-4 p-4">
                      {/* 比赛名称 */}
                      <div className="col-span-12 flex items-center gap-3 lg:col-span-5">
                        <div className="accent-bg flex h-7 min-w-[28px] items-center justify-center rounded-md px-1 text-sm text-white">{index + 1 + limit * (page - 1)}</div>
                        <div className="min-w-0 flex-grow">
                          <div className="flex items-center">
                            <h3 className="my-auto mr-2 max-w-[calc(100%-30px)] truncate text-base font-semibold text-gray-800 group-hover:text-blue-600">{match.name}</h3>
                          </div>

                          {/* 移动端信息显示优化 */}
                          <div className="mt-2 flex items-center gap-3 lg:hidden">
                            <div className="flex min-w-0 flex-1 items-center rounded-md bg-gray-50 px-2 py-1">
                              <IconFont icon="icon-calendar" className="mr-1 flex-shrink-0 text-xs text-gray-500" />
                              <span className="truncate text-xs text-gray-600">
                                {match.match_time ? dayjs(match.match_time).format('YYYY/MM/DD') : dayjs(match.created_at).format('YYYY/MM')}
                              </span>
                            </div>

                            {/* 显示人数信息 */}
                            <div className="flex min-w-0 flex-1 items-center rounded-md bg-gray-50 px-2 py-1">
                              <IconFont icon="icon-team" className="mr-1 flex-shrink-0 text-xs text-gray-500" />
                              <span className="truncate text-xs text-gray-600">{getMatchScale(match)}</span>
                            </div>

                            <div className="flex flex-shrink-0 items-center">
                              <span
                                className={`inline-flex items-center rounded-full bg-gradient-to-r ${getMatchTypeColor(match.match_type)} whitespace-nowrap px-2 py-0.5 text-xs`}
                              >
                                {`${match.allow_online_game ? '线上' : ''}${matchTypeMap[match.match_type]}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 时间 */}
                      <div className="hidden items-center text-gray-700 lg:col-span-2 lg:flex">
                        {match.match_time ? dayjs(match.match_time).format('YYYY/MM/DD') : dayjs(match.created_at).format('YYYY/MM')}
                      </div>

                      {/* 人数 */}
                      <div className="hidden items-center text-gray-700 lg:col-span-1 lg:flex">
                        <span>{getMatchScale(match)}</span>
                      </div>

                      {/* 赛制 - 根据比赛类型应用不同的颜色 */}
                      <div className={`hidden lg:flex ${isAdmin ? 'lg:col-span-2' : 'lg:col-span-2'}`}>
                        <span className={`inline-flex items-center rounded-full bg-gradient-to-r ${getMatchTypeColor(match.match_type)} px-2.5 py-0 text-xs font-medium`}>
                          {`${match.allow_online_game ? '线上' : ''}${matchTypeMap[match.match_type]}`}
                        </span>
                      </div>

                      {/* 创建者 */}
                      <div className={`hidden items-center truncate text-gray-700 lg:flex ${isAdmin ? 'lg:col-span-2' : 'lg:col-span-2'}`}>
                        <span className="truncate">{match.owner_name}</span>
                      </div>

                      {/* 比赛状态 */}
                      <div className="absolute bottom-0 right-2 top-0 z-20 hidden items-center lg:col-span-1 lg:flex">{getMatchStatus(match)}</div>
                    </div>

                    {/* 底部装饰 */}
                    <div className="absolute bottom-0 h-[2px] w-full bg-gradient-to-r from-transparent via-blue-100 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </Link>
              </div>
              <Divider className="mb-0 mt-3 lg:hidden" />
            </motion.div>
          ))}
        </div>
      </motion.div>
    )
  }

  const renderCardView = () => {
    if (matches.length === 0 && isValidating) {
      return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5">
              <div className="aspect-video animate-pulse bg-gradient-to-br from-neutral-100 to-neutral-200" />
              <div className="p-3">
                <Skeleton active paragraph={{ rows: 1 }} title={{ width: '70%' }} />
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (matches.length === 0 && !isValidating) {
      return (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-12">
          <Empty description="暂无比赛" />
          <Button type="primary" className="accent-button mt-4" onClick={() => (isLogin ? setModalVisible(true) : router.push(`/login?redirect=${router.asPath}`))}>
            创建第一个比赛
          </Button>
        </div>
      )
    }

    // 按原始顺序渲染,不因 poster 加载失败而重排(重排会在滚动中把卡片往末尾挪、造成下方赛事「往上涨」)。
    // 加载失败的 poster 由下方 <img> 的 src 兜底为默认图,原地替换,位置不变。
    return (
      <motion.div
        key="card-view"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
      >
        {matches.map((match, index) => (
          <motion.div
            className="relative"
            key={match.id}
            initial={false}
          >
            <Link href={getMatchLink(match.id, match.match_type)} onClick={(e) => selectionMode && (e.preventDefault(), toggleMatchSelection(match.id))}>
              <div className="group relative aspect-video overflow-hidden rounded-xl bg-gradient-to-br from-neutral-100 to-neutral-200 shadow-sm ring-1 ring-black/5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl">
                {/* 背景图:poster 优先,无则默认灵缇图;挂载时 CSS 动画淡入(静止态 opacity=1,绝不会卡成透明)。
                    lazy+async decode:屏外图片不在滚动时同步解码,滚动更顺滑。 */}
                <img
                  src={match.poster && !brokenPosters.has(match.id) ? match.poster : '/match-default-bg.png'}
                  alt={match.name}
                  loading="lazy"
                  decoding="async"
                  onError={() => setBrokenPosters((prev) => (prev.has(match.id) ? prev : new Set(prev).add(match.id)))}
                  className="poster-fade-in absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5" />

                {selectionMode && (
                  <div className="absolute left-2 top-2 z-30">
                    <Checkbox
                      checked={selectedMatches.includes(match.id)}
                      onChange={() => toggleMatchSelection(match.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5"
                    />
                  </div>
                )}

                <div className="absolute right-2 top-2 z-20">{getMatchStatus(match)}</div>

                <div className="absolute inset-x-0 bottom-0 z-10 p-3 text-white">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="accent-bg flex h-6 min-w-[24px] items-center justify-center rounded-md px-1 text-xs text-white">{index + 1 + limit * (page - 1)}</div>
                    <div className="truncate text-base font-bold">{match.name}</div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/85">
                    <span>{match.match_time ? dayjs(match.match_time).format('YYYY.MM.DD') : dayjs(match.created_at).format('YYYY.MM')}</span>
                    <span className={`rounded-full bg-gradient-to-r ${getMatchTypeColor(match.match_type)} px-2 py-0.5 font-medium`}>
                      {`${match.allow_online_game ? '线上' : ''}${matchTypeMap[match.match_type]}`}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    )
  }

  const renderMyMatchesSection = () => {
    if (!isLogin) return null
    const myMatches = myMatchesData?.matches ?? []

    return (
      <motion.div
        className="relative my-6 overflow-hidden rounded-2xl border border-transparent bg-gradient-to-br from-blue-600/20 via-indigo-500/10 to-purple-500/10 p-[1px] shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <div className="rounded-2xl border border-white/20 bg-white/90 p-5 backdrop-blur-md">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-2xl font-semibold text-gray-900">
                我的赛事
                <span className="ml-2 text-sm font-normal text-gray-500">共 {totalMyMatches} 场</span>
              </div>
              <p className="text-sm text-gray-500">快速回到你创建或管理的比赛，方便继续管理和查看。</p>
            </div>
            <div className="flex flex-wrap gap-2 self-start">
              <Button type="primary" className="accent-button" onClick={() => setModalVisible(true)}>
                创建比赛
              </Button>
            </div>
          </div>
          {isMyMatchesValidating ? (
            <Skeleton active className="mt-4" paragraph={{ rows: 4 }} />
          ) : myMatches.length > 0 ? (
            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {myMatches.map((match) => (
                  <motion.div key={match.id} initial={false}>
                    <Link href={getMatchLink(match.id, match.match_type)} className="group block">
                      <div className="flex h-full flex-col justify-between gap-3 rounded-2xl border border-white bg-white/90 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">{match.name}</span>
                            <span
                              className={`inline-flex flex-shrink-0 items-center rounded-full bg-gradient-to-r ${getMatchTypeColor(
                                match.match_type
                              )} px-2 py-0.5 text-xs font-medium`}
                            >
                              {`${match.allow_online_game ? '线上' : ''}${matchTypeMap[match.match_type]}`}
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <IconFont icon="icon-calendar" className="text-gray-400" />
                              <span>{match.match_time ? dayjs(match.match_time).format('YYYY/MM/DD') : '时间待定'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <IconFont icon="icon-team" className="text-gray-400" />
                              <span>{getMatchScale(match)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>创建于 {dayjs(match.created_at).format('YYYY/MM/DD')}</span>
                          {/* 我的赛事:整张卡片已链接到比赛页,这里只显示状态,不再放按钮(原「立即进入」实际跳报名页,有歧义) */}
                          {match.finished ? (
                            <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">已结束</span>
                          ) : match.stop_entry ? (
                            <span className="shrink-0 rounded-full bg-yellow-50 px-2.5 py-1 text-xs font-medium text-yellow-700">报名截止</span>
                          ) : match.show_entry_path ? (
                            <span className="shrink-0 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">报名中</span>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
              {totalMyMatches > PERSONAL_MATCHES_LIMIT && (
                <div className="mt-2 flex justify-end">
                  <Pagination
                    size="small"
                    showSizeChanger={false}
                    total={totalMyMatches}
                    pageSize={PERSONAL_MATCHES_LIMIT}
                    current={myMatchesPage}
                    onChange={(page) => setMyMatchesPage(page)}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-white/70 p-8 text-center shadow-sm">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-2xl text-indigo-500">
                <IconFont icon="icon-edit" />
              </div>
              <div className="mt-3 text-xl font-semibold text-gray-900">暂无我的赛事</div>
              <p className="mt-2 text-sm text-gray-500">创建一场新赛事，邀请队友加入并开始管理赛程。</p>
              <Button type="primary" className="accent-button mt-4 h-10 rounded-xl px-10" onClick={() => setModalVisible(true)}>
                立即创建
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    )
  }

  const listHeaderTitle =
    normalizedMatchType === 'test'
      ? '测试比赛'
      : normalizedMatchType === 'my'
      ? '我的比赛'
      : normalizedMatchType === 'refereed'
      ? '我裁判的比赛'
      : '全部比赛'

  return (
    <div className="match-page match-theme-red relative h-full overflow-auto">
      <HomeBackground />
      <div className="relative z-10 mx-auto px-4 py-6">
        <WujiePromoBar className="mb-4" />
        <Card className="overflow-hidden rounded-xl shadow-sm">
          {renderMyMatchesSection()}
          <div className="my-4 space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-1">
                <div className="text-xl font-bold text-gray-800">{listHeaderTitle}</div>
                <div className="text-sm text-gray-500">（共 {sum} 场比赛）</div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <Radio.Group
                  value={normalizedMatchType}
                  onChange={(e) => {
                    router.replace(`/match?match_type=${e.target.value}&page=1&limit=${limit}&keywords=${keywords}`)
                  }}
                  className="flex flex-wrap gap-2"
                >
                  <Radio.Button value="all">全部比赛</Radio.Button>
                  <Radio.Button value="my" disabled={!isLogin}>
                    我的比赛
                  </Radio.Button>
                  <Radio.Button value="refereed" disabled={!isLogin}>
                    我裁判的
                  </Radio.Button>
                  <Radio.Button value="test">测试比赛</Radio.Button>
                </Radio.Group>
                {normalizedMatchType === 'all' && (
                  <Select
                    value={sortBy}
                    onChange={changeSort}
                    className="w-full sm:w-36"
                    options={[
                      { value: 'default', label: '综合排序' },
                      { value: 'created', label: '按创建时间' },
                    ]}
                  />
                )}
                <Input.Search
                  placeholder="搜索比赛"
                  defaultValue={keywords}
                  className="w-full sm:w-48 lg:w-64"
                  enterButton="搜索"
                  allowClear
                  onSearch={(value) => {
                    router.replace(`/match?match_type=${normalizedMatchType}&page=1&keywords=${value}&limit=${limit}`)
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="primary"
                  className="accent-button flex items-center justify-center gap-2"
                  onClick={() => (isLogin ? setModalVisible(true) : router.push(`/login?redirect=${router.asPath}`))}
                >
                  创建比赛
                </Button>
                <Button disabled={!isAdmin} className="accent-border flex items-center justify-center gap-2 border" onClick={() => setTemplateModalVisible(true)}>
                  创建模板比赛
                </Button>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                {isAdmin && (
                  <Button type="primary" ghost={selectionMode} className="accent-border accent-text" onClick={toggleSelectionMode}>
                    {selectionMode ? '取消多选' : '多选'}
                  </Button>
                )}
                {selectionMode && selectedMatches.length > 0 && isAdmin && (
                  <Button danger className="flex items-center gap-1" onClick={onDeleteMultipleMatches}>
                    <IconFont icon="icon-delete" />
                    删除 ({selectedMatches.length})
                  </Button>
                )}
                <motion.div
                  className="accent-border flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-solid transition-colors hover:opacity-80"
                  onClick={() => handleViewModeChange(viewMode === 'list' ? 'card' : 'list')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconFont icon={viewMode === 'list' ? 'icon-dashboard' : 'icon-menu'} className="accent-text text-lg" />
                </motion.div>
              </div>
            </div>
          </div>
          <div className="min-h-[400px]">{viewMode === 'list' ? renderListView() : renderCardView()}</div>

          <div className="mt-6 flex justify-end">
            <Pagination
              defaultCurrent={1}
              total={sum}
              pageSize={limit}
              current={page}
              onChange={(c, s) => {
                router.replace(`/match?match_type=${normalizedMatchType}&page=${c}&limit=${s}&keywords=${keywords}`)
              }}
            />
          </div>
        </Card>
      </div>

      {modalVisible && (
        <Modal
          open={modalVisible}
          width={960}
          onCancel={() => {
            setModalVisible(false)
            setSelectedMatchType('team')
            setIsTestMatch(false)
            setGameCategory('card')
          }}
          title="创建比赛"
          footer={false}
        >
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900">创建新比赛</h3>
              <p className="text-sm text-gray-500">按三步填写；带 * 的必填，其余留空也能创建，建后可在「比赛设置」继续补全。</p>
            </div>

            {/* ── 第 1 步：赛事性质 ───────────────────────────────────────────────
                放在最前面，因为它决定后面所有事：正式赛永久保存并计费，测试赛限时销毁。
                以前它藏在表单最底下一个「否 / 是」单选里，用户经常没看见就建成了测试赛。 */}
            <section>
              <div className="mb-3 flex items-baseline gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#DB3142] text-xs font-semibold text-white">1</span>
                <span className="text-base font-semibold text-gray-900">赛事性质</span>
                <span className="text-xs text-gray-400">先确认这是正式比赛还是试用，二者的保存策略与计费完全不同</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  {
                    test: false,
                    title: '正式比赛',
                    desc: '永久保存 · 可出电子证书/成绩册 · 计入公开排名与检索',
                    foot:
                      quota && !quota.needs_mobile
                        ? `今日还可创建 ${quota.formal.remaining} / ${quota.formal.limit} 场`
                        : '需先绑定手机号',
                    disabled: false,
                  },
                  {
                    test: true,
                    title: '测试赛（试用）',
                    desc: `熟悉系统用 · 创建后 ${testTtlMinutes} 分钟自动删除 · 不可转为正式赛`,
                    foot: quota
                      ? `今日还可创建 ${quota.test.remaining} / ${quota.test.limit} 场 · ${
                          quota.test.free ? '包年包月会员免费' : '按正式赛价格的 1/5 计费'
                        }`
                      : '按正式赛价格的 1/5 计费',
                    disabled: false,
                  },
                ].map((opt) => (
                  <div
                    key={String(opt.test)}
                    role="button"
                    tabIndex={0}
                    onClick={() => setIsTestMatch(opt.test)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') setIsTestMatch(opt.test)
                    }}
                    className={`cursor-pointer rounded-2xl border-2 border-solid p-4 transition-all ${
                      isTestMatch === opt.test
                        ? opt.test
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-[#DB3142] bg-[#DB3142]/5'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 border-solid ${
                          isTestMatch === opt.test ? (opt.test ? 'border-amber-500' : 'border-[#DB3142]') : 'border-gray-300'
                        }`}
                      >
                        {isTestMatch === opt.test && (
                          <span className={`h-2 w-2 rounded-full ${opt.test ? 'bg-amber-500' : 'bg-[#DB3142]'}`} />
                        )}
                      </span>
                      <span className="text-base font-semibold text-gray-900">{opt.title}</span>
                    </div>
                    <p className="mt-2 mb-0 text-sm leading-relaxed text-gray-600">{opt.desc}</p>
                    <p className="mt-2 mb-0 text-xs font-medium text-gray-500">{opt.foot}</p>
                  </div>
                ))}
              </div>
              {isTestMatch && (
                <div className="mt-3 flex items-start rounded-2xl border border-solid border-amber-300 bg-amber-50 p-4">
                  <div className="text-sm leading-relaxed text-amber-900">
                    <div className="text-base font-semibold">本场将在创建 {testTtlMinutes} 分钟后自动删除</div>
                    <div className="mt-1">
                      测试赛仅供熟悉系统流程，到点后连同选手、对局、成绩一并移除，且<b>不能转为正式比赛</b>。
                      要办真实赛事请选「正式比赛」，否则成绩会全部丢失。
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ── 第 2 步：基础信息 ─────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              <div className="flex flex-1 items-center gap-2">
                <IconFont icon="icon-token" className="accent-text" />
                <span>
                  灵缇点剩余：<span className="text-base font-semibold text-gray-900">{current_user.lingti_tokens}</span>
                </span>
              </div>
              <div className="flex flex-1 items-center gap-2">
                <IconFont icon="icon-info-circle" className="accent-text" />
                <span>
                  本次创建需消耗{' '}
                  <span className={`text-base font-semibold ${currentCost > 0 ? 'text-[#DB3142]' : 'text-gray-900'}`}>
                    {currentCost > 0 ? `${currentCost} 点` : '0 点（免费）'}
                  </span>
                </span>
              </div>
              <div className="flex flex-1 items-center gap-2">
                <IconFont icon="icon-info-circle" className="accent-text" />
                <span>
                  {isTestMatch ? (
                    quota ? (
                      <>
                        今日还可创建{' '}
                        <span className={`text-base font-semibold ${quota.test.remaining === 0 ? 'text-red-500' : 'text-gray-900'}`}>
                          {quota.test.remaining}
                        </span>{' '}
                        / {quota.test.limit} 场测试赛（不占用正式比赛额度）
                      </>
                    ) : (
                      <>测试赛不占用正式比赛额度</>
                    )
                  ) : quota && !quota.needs_mobile ? (
                    <>
                      今日还可创建{' '}
                      <span className={`text-base font-semibold ${quota.formal.remaining === 0 ? 'text-red-500' : 'text-gray-900'}`}>
                        {quota.formal.remaining}
                      </span>{' '}
                      / {quota.formal.limit} 场正式比赛
                      {quota.formal.remaining === 0 && (
                        <Link href="/vip_billing" className="accent-text ml-2 underline">升级提额 →</Link>
                      )}
                    </>
                  ) : (
                    <>创建成功后可在设置页继续完善报名、权限等内容。</>
                  )}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-6 lg:flex-row">
              <Form
                className="flex-1"
                layout="vertical"
                form={createMatchForm}
                initialValues={{
                  match_time: dayjs(),
                  type: 'team',
                }}
                onFinish={(e) => {
                  debouncedFunction(() => {
                    createMatch({ ...e, is_test_match: isTestMatch }).then((res) => {
                      if (res.ok) {
                        if (isTestMatch) notifyTestMatchCreated(res.match)
                        // 活动抽奖类型跳转到活动设置页面，其他类型跳转到通用设置页面
                        if (e.type === 'banquet') {
                          router.push(`/match/${res.match.id}/banquet/settings`)
                        } else {
                          router.push(`/match/${res.match.id}/settings`)
                        }
                      } else {
                        showCreateError(res.message)
                      }
                    })
                  })
                }}
              >
                <div className="mb-3 flex items-baseline gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#DB3142] text-xs font-semibold text-white">2</span>
                  <span className="text-base font-semibold text-gray-900">基础信息</span>
                </div>
                <Form.Item
                  label="比赛名"
                  name="match_name"
                  rules={[
                    { required: true, message: '请输入比赛名称' },
                    { max: 60, message: '比赛名不超过 60 字' },
                  ]}
                  extra="建议写全：年份 + 主办方 + 赛事名，例如「2026 灵缇互娱春季掼蛋公开赛」"
                >
                  <Input autoFocus showCount maxLength={60} placeholder="例：2026 灵缇互娱春季掼蛋公开赛" />
                </Form.Item>
                <Tabs
                  className="mb-0"
                  size="small"
                  activeKey={gameCategory}
                  onChange={(key: 'card' | 'pickleball') => {
                    setGameCategory(key)
                    const defaultType = key === 'pickleball' ? 'pickleball_group_elimination' : 'team'
                    createMatchForm.setFieldValue('type', defaultType)
                    setSelectedMatchType(defaultType)
                  }}
                  items={[
                    { key: 'card', label: '♠️ 棋牌赛事' },
                    { key: 'pickleball', label: '🏓 其他赛事' },
                  ]}
                />
                <Form.Item label="" name="type" rules={[{ required: true, message: '请选择比赛类型' }]}>
                  <Select
                    showSearch
                    optionFilterProp="title"
                    onChange={(e) => {
                      setSelectedMatchType(e)
                    }}
                    options={(gameCategory === 'pickleball' ? otherMatchTypeOrder : cardMatchTypeOrder).map((value) => {
                      // 软门槛:够档位免费、低于档位扣点即可创建——这里只展示该场要扣的点数(0=免费)
                      const cost = costOfType(value)
                      const name = onlineMatchTypeMap[value] ?? matchTypeMap[value] ?? value
                      return {
                        value,
                        title: name,
                        label: (
                          <span className="flex w-full items-center justify-between gap-3">
                            <span>{name}</span>
                            <span className={cost > 0 ? 'text-[#DB3142]' : 'text-gray-400'}>
                              {value.startsWith('online_') ? '30点/人' : cost > 0 ? `${cost}点/场` : '免费'}
                            </span>
                          </span>
                        ),
                      }
                    })}
                  />
                </Form.Item>

                <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-600">
                  <span className="mr-2 font-semibold text-gray-800">赛制介绍:</span>
                  <span>{matchTypeIntro[selectedMatchType] ?? '暂无介绍'}</span>
                </div>
                {gameCategory === 'card' ? (
                  <Form.Item
                    label="核心规则"
                    name="core_rule_slug"
                    tooltip="从灵缇规则库选择，可不选；建后可在赛事设置中修改"
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="从规则库选择（可不选）"
                      options={coreRules.map((r) => ({ value: r.slug, label: r.title }))}
                    />
                  </Form.Item>
                ) : null}
                {selectedMatchType === 'group_round_robin' ? (
                  <Form.Item
                    label="分几组"
                    name="group_count"
                    initialValue={4}
                    tooltip="X 队会尽量平均分到这么多组，每组内部两两循环；种子队会被分到不同组。建后也可在赛事设置里调整。"
                    rules={[{ required: true, message: '请填写组数' }]}
                  >
                    <InputNumber min={2} max={32} className="w-full" placeholder="例：4（24 队分 4 组、每组 6 队）" />
                  </Form.Item>
                ) : null}
                <Form.Item label="比赛开始时间" name="match_time" rules={[{ required: true, message: '请选择比赛时间' }]}>
                  <CustomedDatePicker showTime />
                </Form.Item>

                {/* ── 第 3 步：主办方信息（全部选填） ─────────────────────────────
                    这些字段会出现在赛事信息页、投屏页与成绩册页眉，正式赛建议填全。 */}
                <div className="mb-3 mt-6 flex items-baseline gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 text-xs font-semibold text-white">3</span>
                  <span className="text-base font-semibold text-gray-900">主办方与场地</span>
                  <span className="text-xs text-gray-400">全部选填，会展示在赛事信息页、投屏页与成绩册页眉</span>
                </div>
                <Collapse
                  ghost
                  className="mb-2 rounded-2xl border border-solid border-gray-200 bg-gray-50"
                  items={[
                    {
                      key: 'org',
                      label: <span className="text-sm text-gray-700">展开填写举办地点、主办 / 承办 / 协办 / 赞助单位（选填）</span>,
                      children: (
                        <div className="grid gap-x-4 sm:grid-cols-2">
                          <Form.Item label="举办地点" name="location">
                            <Input placeholder="例：杭州市西湖区文三路 XX 号 3 楼棋牌厅" />
                          </Form.Item>
                          <Form.Item label="指导单位" name="guide_unit">
                            <Input placeholder="选填" />
                          </Form.Item>
                          <Form.Item label="主办单位" name="organize_unit">
                            <Input placeholder="选填" />
                          </Form.Item>
                          <Form.Item label="承办单位" name="undertake_unit">
                            <Input placeholder="选填" />
                          </Form.Item>
                          <Form.Item label="协办单位" name="co_organize_unit">
                            <Input placeholder="选填" />
                          </Form.Item>
                          <Form.Item label="赞助单位" name="sponsor_unit">
                            <Input placeholder="选填" />
                          </Form.Item>
                        </div>
                      ),
                    },
                  ]}
                />
                <Form.Item className="pt-2">
                  <div className="flex flex-col gap-3 rounded-xl sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs leading-relaxed text-gray-500">
                      {isTestMatch ? (
                        <>
                          将创建<b className="text-amber-600">测试赛</b>，
                          {testTtlMinutes} 分钟后自动删除，消耗 <b>{currentCost}</b> 点
                        </>
                      ) : (
                        <>
                          将创建<b className="text-[#DB3142]">正式比赛</b>，永久保存，消耗 <b>{currentCost}</b> 点
                        </>
                      )}
                    </div>
                    <div className="flex flex-col gap-3 text-center sm:flex-row sm:justify-end">
                      <Button onClick={() => setModalVisible(false)} className="h-10 flex-1 sm:flex-none">
                        取消
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="h-10 flex-1 rounded-xl border-[#dc2626] bg-[#dc2626] px-10 text-white shadow-lg hover:bg-[#b91c1c] sm:min-w-[140px] sm:flex-none"
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-full bg-green-300" />
                          {isTestMatch ? '创建测试赛' : '确认创建'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </Form.Item>
              </Form>
              {vipLevel <= VIP_LEVEL.SSVIP && (
                <div className="lg:w-[320px]">
                  <VipInfo className="lg:mb-0" />
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
      {templateModalVisible && (
        <Modal open={templateModalVisible} onCancel={() => setTemplateModalVisible(false)} title="创建模板比赛" footer={false}>
          <Form
            layout="vertical"
            onFinish={(params) => {
              debouncedFunction(() => {
                post('matches/create_match_from_template', params).then((res) => {
                  if (res.ok) {
                    router.push(`/match/${res.match.id}/settings`)
                  } else {
                    message.error(res.message, 15)
                  }
                })
              })
            }}
          >
            <Form.Item label="比赛类型" name="template" initialValue="lt730" rules={[{ required: true, message: '请选择比赛类型' }]}>
              <Select
                options={[
                  {
                    value: 'lt730',
                    label: '灵缇七点半',
                  },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button onClick={() => setTemplateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" className="ml-4">
                提交
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  )
}

export const getServerSideProps = async (ctx) => {
  return {
    props: {
      keywords: ctx.query.keywords ?? '',
      match_type: ctx.query.match_type ?? 'all',
      page: ctx.query.page ?? '1',
      limit: ctx.query.limit ?? '20',
    },
  }
}
Object.assign(Matches as any, {
  title: '灵缇赛事系统 · 掼蛋/匹克球/网球等赛事一键编排',
  description: '在灵缇赛事系统浏览或创建您的赛事：棋牌类（掼蛋/桥牌/升级），球拍类（匹克球/网球/羽毛球/乒乓球）小组淘汰赛、循环赛、淘汰赛，多种赛制一键编排。',
})

export default Matches
